import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  HostBinding,
  Input,
} from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { ContainerSize } from '@freelancer/ui/container';
import { HorizontalAlignment } from '@freelancer/ui/grid';
import { SectionHeroCustomContentComponent } from './section-hero-custom-content.component';
import { SectionHeroDescriptionComponent } from './section-hero-description.component';
import { SectionHeroDetailTextComponent } from './section-hero-detail-text.component';
import { SectionHeroImageDetailComponent } from './section-hero-image-detail.component';
import { SectionHeroLogoComponent } from './section-hero-logo.component';
import { SectionHeroVideoBackgroundComponent } from './section-hero-video-background.component';
import {
  SectionHeroAlignment,
  SectionHeroBackgroundColor,
  SectionHeroContentPosition,
  SectionHeroContentWidth,
  SectionHeroHeight,
} from './section-hero.types';

@Component({
  standalone: false,
  selector: 'fl-section-hero',
  template: `
    @if (sectionHeroVideoBackground) {
      <ng-content select="fl-section-hero-video-background"></ng-content>
    }
    <fl-container
      class="SectionHeroContainer"
      [size]="containerSize"
      [sizeTablet]="containerSizeTablet"
      [sizeDesktopSmall]="containerSizeDesktopSmall"
    >
      <div [class.desktop:hide]="!!sectionHeroVideoBackground">
        <ng-content select="fl-section-hero-background-image"></ng-content>
      </div>
      <fl-grid
        class="SectionHeroGrid"
        [hAlign]="
          alignment == SectionHeroAlignment.CENTER
            ? HorizontalAlignment.HORIZONTAL_CENTER
            : undefined
        "
        [flAnimateOnScroll]="animateOnScroll"
        [flAnimateOnScrollBehavior]="animateOnScrollBehavior"
      >
        @if (sectionHeroImageDetail) {
          <fl-col
            class="SectionHeroGrid-detail mb-small tablet:mb-none"
            [col]="12"
            [colDesktopSmall]="6"
            [colTabletOrder]="2"
          >
            <div class="ImageDetail">
              <ng-content select="fl-section-hero-image-detail"></ng-content>
            </div>
          </fl-col>
        }
        <fl-col
          class="SectionHeroGrid-content"
          [attr.data-content-position]="contentPosition"
          [col]="12"
          [colTablet]="sectionHeroImageDetail ? 6 : contentWidth"
          [colTabletOrder]="1"
        >
          @if (sectionHeroLogo) {
            <div class="mb-xsmall">
              <ng-content select="fl-section-hero-logo"></ng-content>
            </div>
          }
          <h1
            class="text-title-32 tablet:text-title-40 desktop:text-title-50 desktop_xlarge:text-title-60 mb-small desktop:mb-mid"
            [class.text-light]="
              backgroundColor === SectionHeroBackgroundColor.DARK
            "
            [class.text-dark]="
              !(backgroundColor === SectionHeroBackgroundColor.DARK)
            "
          >
            <ng-content select="fl-section-hero-title"></ng-content>
          </h1>
          <div class="DarkText">
            @if (sectionHeroDescription) {
              <div
                class="text-body-16 desktop:text-body-20 font-normal mb-small desktop:mb-mid"
                [class.text-color-inherit]="
                  !(backgroundColor === SectionHeroBackgroundColor.DARK)
                "
                [class.text-light]="
                  backgroundColor === SectionHeroBackgroundColor.DARK
                "
              >
                <ng-content select="fl-section-hero-description"></ng-content>
              </div>
            }
            @if (sectionHeroDetailText) {
              <div
                class="text-body-16 font-normal desktop:text-body-16 tablet:text-body-16 mb-small desktop:mb-mid"
                [class.text-color-inherit]="
                  !(backgroundColor === SectionHeroBackgroundColor.DARK)
                "
                [class.text-light]="
                  backgroundColor === SectionHeroBackgroundColor.DARK
                "
              >
                <ng-content select="fl-section-hero-detail-text"></ng-content>
              </div>
            }
          </div>
          <ng-content></ng-content>
        </fl-col>
        @if (sectionHeroCustomContent) {
          <fl-col
            class="SectionHeroGrid-content"
            [col]="12"
          >
            <ng-content select="fl-section-hero-custom-content"></ng-content>
          </fl-col>
        }
      </fl-grid>
    </fl-container>
  `,
  styleUrls: ['./section-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHeroComponent {
  HorizontalAlignment = HorizontalAlignment;
  SectionHeroAlignment = SectionHeroAlignment;
  SectionHeroBackgroundColor = SectionHeroBackgroundColor;

  @HostBinding('attr.data-alignment')
  @Input()
  alignment: SectionHeroAlignment = SectionHeroAlignment.LEFT;

  @HostBinding('attr.data-content-position')
  @Input()
  contentPosition: SectionHeroContentPosition =
    SectionHeroContentPosition.CENTER;

  @HostBinding('attr.data-height')
  @Input()
  height?: SectionHeroHeight;

  @HostBinding('attr.data-height-tablet')
  @Input()
  heightTablet?: SectionHeroHeight;

  @HostBinding('attr.data-height-desktop')
  @Input()
  heightDesktop?: SectionHeroHeight;

  @Input() contentWidth: SectionHeroContentWidth =
    SectionHeroContentWidth.SMALL;

  @Input() containerSize: ContainerSize = ContainerSize.DESKTOP_LARGE;
  @Input() containerSizeTablet?: ContainerSize;
  @Input() containerSizeDesktopSmall?: ContainerSize;

  @HostBinding('attr.data-background-color')
  @Input()
  backgroundColor = SectionHeroBackgroundColor.DARK;

  @Input() animateOnScroll = false;
  @Input() animateOnScrollBehavior: AnimationBehavior =
    AnimationBehavior.FADE_FROM_BOTTOM;

  @ContentChild(SectionHeroDescriptionComponent)
  sectionHeroDescription: SectionHeroDescriptionComponent;
  @ContentChild(SectionHeroDetailTextComponent)
  sectionHeroDetailText: SectionHeroDetailTextComponent;
  @ContentChild(SectionHeroLogoComponent)
  sectionHeroLogo: SectionHeroLogoComponent;
  @ContentChild(SectionHeroImageDetailComponent)
  sectionHeroImageDetail: SectionHeroImageDetailComponent;
  @ContentChild(SectionHeroCustomContentComponent)
  sectionHeroCustomContent: SectionHeroCustomContentComponent;
  @ContentChild(SectionHeroVideoBackgroundComponent)
  sectionHeroVideoBackground: SectionHeroVideoBackgroundComponent;
}
